/* ==========================================================================
   Mijn Huisdier Cadeau — Kerst-producttemplate (geen externe libraries)
   - <mhc-xmas-personalisation>: visuele kerstdetail-kaarten die het bestaande
     veld van VO Product Options (bcpo) aansturen. Alleen als dat veld niet
     bestaat, wordt een eigen line item property gebruikt (fallback).
   - <mhc-xmas-deadline>: verbergt zichzelf na de ingestelde datum.
   - <mhc-xmas-delivery>: berekent verwachte verzenddatums (werkdagen).
   - <mhc-xmas-sticky>: mobiele balk die naar de personalisatie scrollt
     (voegt nooit zelf toe aan de winkelwagen).
   ========================================================================== */
(() => {
  if (window.__mhcXmasLoaded) return;
  window.__mhcXmasLoaded = true;

  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  /* ---------------- Personalisatie ---------------- */
  class MhcXmasPersonalisation extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.mode = 'pending'; // pending | app | fallback | none
      this.inputs = Array.from(this.querySelectorAll('.mhc-x-card__input'));
      this.errorEl = this.querySelector('[data-error]');
      this.summary = this.querySelector('[data-summary]');
      this.fieldMatch = norm(this.dataset.appField);
      this.uploadMatch = norm(this.dataset.uploadField);
      this.textMatch = norm(this.dataset.textField);
      this.hideAppField = this.dataset.hideAppField === 'true';
      this.allowFallback = this.dataset.fallback === 'true';
      this.fallbackName = this.dataset.fallbackName || 'Kerstdetail';
      this.designMode = this.dataset.designMode === 'true';

      this.inputs.forEach((input) =>
        input.addEventListener('change', () => this.onCardChange(input))
      );

      this.form = this.findForm();
      if (this.dataset.source === 'native') {
        // Eigen veld (zonder app): direct een line item property gebruiken
        this.allowFallback = true;
        this.useFallbackOrHide();
      } else {
        this.waitForApp();
      }
    }

    findForm() {
      const section = this.closest('.shopify-section') || document;
      return (
        section.querySelector('form[data-type="add-to-cart-form"]') ||
        document.querySelector('form[data-type="add-to-cart-form"]')
      );
    }

    appRadios() {
      if (!this.form || !this.fieldMatch) return [];
      const all = document.querySelectorAll('input[name^="properties["]');
      return Array.from(all).filter(
        (i) =>
          (i.type === 'radio' || i.type === 'checkbox') &&
          (i.form === this.form || this.form.contains(i)) &&
          norm(i.name).includes(this.fieldMatch)
      );
    }

    appSelect() {
      if (!this.form || !this.fieldMatch) return null;
      return (
        Array.from(this.form.querySelectorAll('select[name^="properties["]')).find((s) =>
          norm(s.name).includes(this.fieldMatch)
        ) || null
      );
    }

    waitForApp() {
      const start = Date.now();
      const check = () => {
        if (!this.form) this.form = this.findForm();
        const radios = this.appRadios();
        const select = this.appSelect();
        if (radios.length || select) {
          this.useApp(radios, select);
          return true;
        }
        if (Date.now() - start > 9000) {
          this.useFallbackOrHide();
          return true;
        }
        return false;
      };
      if (check()) return;
      const obs = new MutationObserver(() => {
        if (check()) obs.disconnect();
      });
      obs.observe(this.form || document.body, { childList: true, subtree: true });
      const timer = setInterval(() => {
        if (this.mode !== 'pending' || check()) {
          clearInterval(timer);
          obs.disconnect();
        }
      }, 600);
    }

    useApp(radios, select) {
      this.mode = 'app';
      this.appRadioEls = radios;
      this.appSelectEl = select;

      const ref = radios[0] || select;
      const wrapper = ref.closest('.selector-wrapper, .bcpo-property, .bcpo-row') || ref.parentElement;
      this.appWrapper = wrapper;
      if (this.hideAppField && wrapper && !this.designMode) wrapper.classList.add('mhc-x-app-field-hidden');

      // Kaarten zonder overeenkomende app-waarde markeren
      const values = radios.length
        ? radios.map((r) => r.value)
        : Array.from(select.options).map((o) => o.value);
      const missing = [];
      this.inputs.forEach((input) => {
        const match = this.matchValue(input.dataset.appValue, values);
        input.dataset.matched = match ? 'true' : 'false';
        if (match) input.dataset.resolved = match;
        else missing.push(input.dataset.label);
      });
      if (missing.length && this.designMode) {
        this.editorNote(
          'Let op (alleen zichtbaar in de editor): de app heeft geen waarde voor: ' +
            missing.join(', ') +
            '. Voeg deze waarde toe in VO Product Options of vul bij de kaart de exacte app-waarde in.'
        );
      }
      if (missing.length && !this.designMode) {
        // Kaarten die de app niet kent, worden niet getoond om fouten te voorkomen
        this.inputs.forEach((input) => {
          if (input.dataset.matched === 'false') input.closest('.mhc-x-card').hidden = true;
        });
      }

      // Twee-richtingen: wijziging in het app-veld -> kaart bijwerken
      const syncFromApp = () => {
        const current = radios.length ? (radios.find((r) => r.checked) || {}).value : select.value;
        this.inputs.forEach((input) => {
          input.checked = !!current && input.dataset.resolved === current;
        });
        this.updateSummary();
      };
      radios.forEach((r) => r.addEventListener('change', syncFromApp));
      if (select) select.addEventListener('change', syncFromApp);

      // Bestaande keuze overnemen, anders kaart-keuze doorzetten
      const checkedCard = this.inputs.find((i) => i.checked);
      if (checkedCard) this.pushToApp(checkedCard);
      else syncFromApp();

      this.bindValidation();
      this.bindSummary();
      this.hidden = false;

      // Als thema of app het formulier opnieuw opbouwt: opnieuw koppelen
      if (!this._reattach) {
        this._reattach = new MutationObserver(() => {
          const stale = (this.appRadioEls || []).some((r) => !r.isConnected) ||
            (this.appSelectEl && !this.appSelectEl.isConnected);
          if (!stale) return;
          this.form = this.findForm();
          const radios = this.appRadios();
          const select = this.appSelect();
          if (radios.length || select) {
            this.useApp(radios, select);
          }
        });
        this._reattach.observe(this.closest('.shopify-section') || document.body, { childList: true, subtree: true });
      }
    }

    matchValue(wanted, values) {
      if (!wanted) return null;
      const w = norm(wanted);
      const strip = (x) => x.replace(/^\d+\s*/, '');
      // 1) exact, 2) exact zonder nummering, 3) langste gedeeltelijke match
      const exact = values.find((v) => norm(v) === w) || values.find((v) => strip(norm(v)) === strip(w));
      if (exact) return exact;
      const partial = values
        .filter((v) => norm(v) && (strip(norm(v)).includes(strip(w)) || strip(w).includes(strip(norm(v)))))
        .sort((a, b) => norm(b).length - norm(a).length);
      return partial[0] || null;
    }

    useFallbackOrHide() {
      if (!this.form) this.form = this.findForm();
      if (!this.form || !this.allowFallback) {
        this.mode = 'none';
        if (this.designMode) {
          this.editorNote(
            'Geen kerstdetail-veld van de personalisatie-app gevonden en de terugvaloptie staat uit. Klanten zien dit blok niet.'
          );
        } else {
          this.hidden = true;
        }
        return;
      }
      this.mode = 'fallback';
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = 'properties[' + this.fallbackName + ']';
      let formId = this.form.getAttribute('id');
      if (!formId) {
        formId = 'mhc-product-form-' + Math.random().toString(36).slice(2, 8);
        this.form.setAttribute('id', formId);
      }
      hidden.setAttribute('form', formId);
      hidden.disabled = true;
      this.appendChild(hidden);
      this.fallbackInput = hidden;
      const checked = this.inputs.find((i) => i.checked);
      if (checked) this.onCardChange(checked);
      this.bindValidation();
      this.bindSummary();
    }

    onCardChange(input) {
      if (!input.checked) return;
      this.showError(false);
      if (this.mode === 'app') this.pushToApp(input);
      if (this.mode === 'fallback' && this.fallbackInput) {
        this.fallbackInput.value = input.dataset.label;
        this.fallbackInput.disabled = false;
      }
      this.updateSummary();
    }

    pushToApp(input) {
      const value = input.dataset.resolved;
      if (!value) return;
      if (this.appRadioEls && this.appRadioEls.length) {
        const target = this.appRadioEls.find((r) => r.value === value);
        if (target && !target.checked) {
          const label = target.id ? document.querySelector('label[for="' + CSS.escape(target.id) + '"]') : null;
          if (label) label.click();
          else target.click();
          if (!target.checked) {
            target.checked = true;
            target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      } else if (this.appSelectEl) {
        this.appSelectEl.value = value;
        this.appSelectEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    hasSelection() {
      return this.inputs.some((i) => i.checked && !i.closest('.mhc-x-card').hidden);
    }

    bindValidation() {
      if (this._validationBound || !this.form) return;
      this._validationBound = true;
      // Capture-fase: draait vóór de themahandler, alleen blokkeren als er niets gekozen is
      document.addEventListener(
        'submit',
        (event) => {
          if (event.target !== this.form) return;
          if (this.mode === 'none' || this.hidden) return;
          if (this.hasSelection()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showError(true);
        },
        true
      );
      this.form.addEventListener(
        'click',
        (event) => {
          const btn = event.target.closest('button[type="submit"], [name="add"]');
          if (!btn || this.mode === 'none' || this.hidden || this.hasSelection()) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.showError(true);
        },
        true
      );
    }

    showError(show) {
      if (!this.errorEl) return;
      this.errorEl.hidden = !show;
      if (show) {
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const first = this.inputs.find((i) => !i.closest('.mhc-x-card').hidden);
        if (first) setTimeout(() => first.focus({ preventScroll: true }), 350);
      }
    }

    placeSummary() {
      // Overzicht direct boven de bestelknop tonen (na upload- en tekstvelden)
      if (!this.summary || !this.form || this.dataset.summaryPosition !== 'button') return;
      const btn = this.form.querySelector('add-to-cart-component, button[name="add"]');
      if (!btn) return;
      const anchor = btn.closest('span') && this.form.contains(btn.closest('span')) ? btn.closest('span') : btn;
      if (anchor.previousElementSibling !== this.summary) anchor.parentNode.insertBefore(this.summary, anchor);
    }

    bindSummary() {
      if (!this.summary || !this.form) return;
      this.placeSummary();
      if (this._summaryBound) {
        this.updateSummary();
        return;
      }
      this._summaryBound = true;
      // Thema kan het formulier opnieuw opbouwen (variantwissel): overzicht terugplaatsen
      new MutationObserver(() => {
        if (!this.summary.isConnected || !this.form.isConnected) {
          if (!this.form.isConnected) this.form = this.findForm();
          if (this.form) this.placeSummary();
        }
      }).observe(this.closest('.shopify-section') || document.body, { childList: true, subtree: true });
      const handler = () => this.updateSummary();
      document.addEventListener('change', (e) => {
        if (e.target && e.target.form === this.form) handler();
      });
      document.addEventListener('input', (e) => {
        if (e.target && e.target.form === this.form) handler();
      });
      this.updateSummary();
    }

    updateSummary() {
      if (!this.summary) return;
      const set = (key, text) => {
        const dd = this.summary.querySelector('[data-summary-' + key + ']');
        if (!dd) return;
        if (text) {
          dd.textContent = text;
          dd.removeAttribute('data-empty');
        } else {
          dd.textContent = dd.dataset.emptyText || '—';
          dd.setAttribute('data-empty', '');
        }
      };

      const card = this.inputs.find((i) => i.checked);
      set('detail', card ? card.dataset.label : '');

      // Foto: bestaand uploadveld van de app
      let photo = '';
      if (this.form) {
        const fields = Array.from(document.querySelectorAll('input[name^="properties["]')).filter(
          (i) => (i.form === this.form || this.form.contains(i)) && (!this.uploadMatch || norm(i.name).includes(this.uploadMatch))
        );
        const file = fields.find((i) => i.type === 'file' && i.files && i.files.length);
        if (file) photo = file.files[0].name;
        else {
          const filled = fields.find((i) => i.type !== 'file' && i.type !== 'radio' && i.value);
          if (filled) photo = this.summary.dataset.photoOk || 'Foto toegevoegd';
        }
      }
      set('photo', photo);

      let text = '';
      if (this.form && this.textMatch) {
        const t = Array.from(document.querySelectorAll('input[name^="properties["], textarea[name^="properties["]')).find(
          (i) => (i.form === this.form || this.form.contains(i)) && norm(i.name).includes(this.textMatch) && i.type !== 'file'
        );
        if (t && t.value.trim()) text = t.value.trim();
      }
      set('text', text);
    }

    editorNote(msg) {
      let note = this.querySelector('.mhc-x-perso__editor-note');
      if (!note) {
        note = document.createElement('p');
        note.className = 'mhc-x-perso__editor-note';
        this.querySelector('.mhc-x-perso').appendChild(note);
      }
      note.textContent = msg;
    }
  }

  /* ---------------- Besteldeadline ---------------- */
  class MhcXmasDeadline extends HTMLElement {
    connectedCallback() {
      const end = Date.parse(this.dataset.end);
      if (!isNaN(end) && Date.now() > end) this.remove();
      else this.hidden = false;
    }
  }

  /* ---------------- Dynamische levertijd ---------------- */
  class MhcXmasDelivery extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min, 10);
      const max = parseInt(this.dataset.max, 10);
      const out = this.querySelector('[data-delivery-text]');
      if (!out || isNaN(min) || isNaN(max)) return;
      const addWorkdays = (date, days) => {
        const d = new Date(date);
        let added = 0;
        while (added < days) {
          d.setDate(d.getDate() + 1);
          const day = d.getDay();
          if (day !== 0 && day !== 6) added++;
        }
        return d;
      };
      const fmt = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' });
      const now = new Date();
      const from = addWorkdays(now, min);
      const to = addWorkdays(now, max);
      const tpl = this.dataset.template || 'Verwachte verzending tussen {van} en {tot}';
      out.textContent = tpl.replace('{van}', fmt.format(from)).replace('{tot}', fmt.format(to));
    }
  }

  /* ---------------- Sticky mobiele balk ---------------- */
  class MhcXmasSticky extends HTMLElement {
    connectedCallback() {
      const btn = this.querySelector('button');
      const target = () =>
        document.querySelector('mhc-xmas-personalisation:not([hidden])') ||
        document.querySelector('form[data-type="add-to-cart-form"]');
      const buy = document.querySelector('form[data-type="add-to-cart-form"] button[name="add"]');
      const persona = document.querySelector('mhc-xmas-personalisation');

      btn.addEventListener('click', () => {
        const t = target();
        if (t) t.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      if (!buy || !('IntersectionObserver' in window)) return;
      const visible = new Set();
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
        const beyondHero = window.scrollY > 200;
        this.classList.toggle('is-visible', visible.size === 0 && beyondHero);
      });
      io.observe(buy);
      if (persona) io.observe(persona);
      window.addEventListener(
        'scroll',
        () => {
          this.classList.toggle('is-visible', visible.size === 0 && window.scrollY > 200);
        },
        { passive: true }
      );
    }
  }

  if (!customElements.get('mhc-xmas-personalisation'))
    customElements.define('mhc-xmas-personalisation', MhcXmasPersonalisation);
  if (!customElements.get('mhc-xmas-deadline')) customElements.define('mhc-xmas-deadline', MhcXmasDeadline);
  if (!customElements.get('mhc-xmas-delivery')) customElements.define('mhc-xmas-delivery', MhcXmasDelivery);
  if (!customElements.get('mhc-xmas-sticky')) customElements.define('mhc-xmas-sticky', MhcXmasSticky);
})();
